import type { Contract, Milestone } from '../types';

/**
 * The contract tab as a status view (ClickUp 869fa5qf8): where a contract
 * stands, whose turn it is, and what is still missing before it can be sent.
 */
export type ContractViewState = 'draft' | 'waiting' | 'signed';

export function contractViewState(contract: Contract): ContractViewState {
  if (
    contract.signingStatus === 'signed' ||
    contract.draftStatus === 'signed_by_both' ||
    (contract.companySigned && contract.personSigned)
  ) return 'signed';
  // A rejected draft or a declined signing is back with the company to change.
  if (contract.draftStatus === 'draft_rejected' || contract.signingStatus === 'declined') return 'draft';
  if (
    contract.draftStatus !== 'none' ||
    contract.companySigned ||
    contract.personSigned ||
    ['sent', 'pending_signature'].includes(contract.signingStatus ?? '')
  ) return 'waiting';
  return 'draft';
}

/** Whose turn it is while a contract waits: the customer reviews a sent draft
 *  and signs after the company; otherwise the company acts. A started signing
 *  nobody has signed is still with the company: the provider invites the
 *  customer only after the company's signature (ClickUp 869fnf0qc). */
export function contractWaitingOn(contract: Contract): 'customer' | 'company' {
  if (contract.draftStatus === 'draft_sent') return 'customer';
  if (contract.personSigned || contract.draftStatus === 'signed_by_person') return 'company';
  if (contract.companySigned || contract.draftStatus === 'signed_by_company') return 'customer';
  return 'company';
}

export type ContractMissingItem = 'stages' | 'stageAmounts' | 'assignment';

/** What a draft still lacks before the company can send it for signing. */
export function contractMissingItems(milestones: Milestone[], assignmentHtml: string, referToAttachments = false): ContractMissingItem[] {
  const stages = milestones.filter(milestone => (milestone.itemType ?? 'milestone') === 'milestone');
  const missing: ContractMissingItem[] = [];
  if (stages.length === 0) missing.push('stages');
  else if (stages.some(stage => !(Number(stage.afterRotAmount ?? stage.amount) > 0))) missing.push('stageAmounts');
  const assignmentText = assignmentHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  if (!assignmentText && !referToAttachments) missing.push('assignment');
  return missing;
}

export type StageSummaryStatus = 'paid' | 'attested' | 'attesting' | 'disputed' | 'inProgress' | 'notStarted';

export function stageSummaryStatus(milestone: Milestone): StageSummaryStatus {
  const status = String(milestone.deliveryStatus ?? '');
  if (status === 'paid' || milestone.paidAt) return 'paid';
  if (['jointly_attested', 'payout_processing', 'payout_failed'].includes(status)) return 'attested';
  if (['ready_for_attestation', 'ready_for_reattestation', 'seller_attested'].includes(status)) return 'attesting';
  if (['under_resolution', 'disputed', 'external_dispute', 'conflict'].includes(status)) return 'disputed';
  if (status === 'in_progress' || milestone.workStartedAt) return 'inProgress';
  return 'notStarted';
}
